/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import types */
import type { LucideIcon } from 'lucide-react';

export type SystemDashboardSectionLabelAccent =
    | 'electricity'
    | 'gas'
    | 'heat'
    | 'photovoltaics'
    | 'report'
    | 'water';

export interface SystemDashboardSectionLabelAnomalies {
    errorCount?: number;
    warningCount?: number;
}

function normalizeAnomalyCount(value: number | undefined): number {
    return value !== undefined && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

interface SystemDashboardSectionLabelProps {
    accent: SystemDashboardSectionLabelAccent;
    anomalies?: SystemDashboardSectionLabelAnomalies | null;
    disabledSensors?: readonly string[] | null;
    icon: LucideIcon;
    label: string;
    onSensorChange?: (sensor: string) => void;
    sensors?: readonly string[] | null;
    to?: string;
}

function SystemDashboardSectionLabel({
    accent,
    anomalies = null,
    disabledSensors = null,
    icon: Icon,
    label,
    onSensorChange,
    sensors = null,
    to,
}: SystemDashboardSectionLabelProps) {
    const { t } = useI18n();
    const [activeSensorIndex, setActiveSensorIndex] = useState(0);
    const warningCount = normalizeAnomalyCount(anomalies?.warningCount);
    const errorCount = normalizeAnomalyCount(anomalies?.errorCount);
    const anomalyCount = warningCount + errorCount;
    const anomalySeverity = errorCount > 0 ? 'error' : warningCount > 0 ? 'warning' : null;
    const hasSensors = sensors !== null && sensors.length > 0;
    const hasMultipleSensors = hasSensors && sensors.length > 1;
    const activeSensor = hasSensors ? sensors[activeSensorIndex % sensors.length] : null;
    const isActiveSensorDisabled = activeSensor !== null && disabledSensors?.includes(activeSensor) === true;

    function showPreviousSensor() {
        if (!sensors || sensors.length < 2) {
            return;
        }

        const previousSensorIndex = (activeSensorIndex - 1 + sensors.length) % sensors.length;
        setActiveSensorIndex(previousSensorIndex);
        onSensorChange?.(sensors[previousSensorIndex]!);
    }

    function showNextSensor() {
        if (!sensors || sensors.length < 2) {
            return;
        }

        const nextSensorIndex = (activeSensorIndex + 1) % sensors.length;
        setActiveSensorIndex(nextSensorIndex);
        onSensorChange?.(sensors[nextSensorIndex]!);
    }

    return (
        <div className={`system-dashboard__section-label${hasSensors ? ' system-dashboard__section-label--with-sensors' : ''}${anomalySeverity ? ` system-dashboard__section-label--anomaly-${anomalySeverity}` : ''}`}>
            {anomalyCount > 0 ? (
                <span
                    className={`system-dashboard__section-anomaly-badge system-dashboard__section-anomaly-badge--${anomalySeverity}`}
                    title={[
                        warningCount > 0 ? `${warningCount} ${t('Warning')}` : null,
                        errorCount > 0 ? `${errorCount} ${t('Error')}` : null,
                    ].filter(Boolean).join(', ')}
                >
                    {anomalyCount}
                </span>
            ) : null}

            {/* Multiple sensors */}
            {hasSensors && hasMultipleSensors && <>
                <div className="flex flex-row items-center justify-center gap-2">
                    <Icon
                        className={`system-dashboard__section-icon system-dashboard__section-icon--with-sensors system-dashboard__section-icon--${accent}`}
                        aria-hidden="true"
                    />
                    <h2 className="mb-0 text-lg font-semibold">{label}</h2>
                </div>
            </>}

            {/* One sensors */}
            {hasSensors && !hasMultipleSensors && <>
                <div className="flex flex-row items-center justify-center gap-2">
                    <Icon
                        className={`system-dashboard__section-icon system-dashboard__section-icon--with-sensor system-dashboard__section-icon--${accent}`}
                        aria-hidden="true"
                    />
                    <h2 className="mb-0 text-lg font-semibold">{label}</h2>
                </div>
            </>}

            {/* No sensors */}
            {!hasSensors && <>
                <Icon
                    className={`system-dashboard__section-icon system-dashboard__section-icon--${accent}`}
                    aria-hidden="true"
                />
                <h2 className="mb-0 text-lg font-semibold">{label}</h2>
            </>}

            {activeSensor ? (
                <div className="flex flex-col items-center gap-0">
                    <div className="system-dashboard__section-sensor">
                        {hasMultipleSensors ? (
                            <button
                                type="button"
                                className="system-dashboard__section-sensor-button"
                                onClick={showPreviousSensor}
                                aria-label="Show previous sensor"
                            >
                                <ChevronLeft aria-hidden="true" />
                            </button>
                        ) : null}
                        <code className="system-dashboard__section-sensor-name">
                            {activeSensor}
                            {isActiveSensorDisabled ? (
                                <sup className="ml-0.5 text-warning" title={t('Disabled sensor')}>*)</sup>
                            ) : null}
                        </code>
                        {hasMultipleSensors ? (
                            <button
                                type="button"
                                className="system-dashboard__section-sensor-button"
                                onClick={showNextSensor}
                                aria-label="Show next sensor"
                            >
                                <ChevronRight aria-hidden="true" />
                            </button>
                        ) : null}
                    </div>
                    {hasMultipleSensors ? (
                        <span className="system-dashboard__section-sensor-pagination">
                            {activeSensorIndex + 1} / {sensors.length}
                        </span>
                    ) : null}
                </div>
            ) : null}

            {to && hasSensors ? (
                <NavLink
                    className="system-dashboard__section-anomaly-link"
                    to={to}
                    aria-label={t('Anomalies')}
                    title={t('Anomalies')}
                >
                    <ArrowRight aria-hidden="true" />
                </NavLink>
            ) : null}
        </div>
    );
}

export default SystemDashboardSectionLabel;
